/**
 * Encryption Error Handler
 * Centralized error handling for E2E encryption operations.
 * Provides user-friendly notifications and production-safe logging.
 */

import debugLogger from './debugLogger';
import monitoringService from '../services/monitoringService';

// Fallback since react-hot-toast might not be installed
const toast = {
    error: (msg) => { /* silent fail */ },
    success: (msg) => { /* silent fail */ },
    loading: (msg) => { /* silent fail */ }
};

// Error severity levels
export const ERROR_SEVERITY = {
    INFO: 'info',
    WARNING: 'warning',
    ERROR: 'error',
    CRITICAL: 'critical'
};

const encryptionErrorHandler = {
    /**
     * Handle encryption process errors
     * @param {Error} error - The error object
     * @param {string} context - Where the error occurred (e.g., 'sending_message')
     */
    handleEncryptionError: (error, context = 'unknown') => {
        debugLogger.error(`Encryption failed [${context}]:`, error);
        monitoringService.logError(error, { type: 'encryption_error', context });

        // User notification for encryption failures (usually prevents sending)
        toast.error('Failed to encrypt message. Please try again.', {
            id: 'encryption-error',
            duration: 4000
        });

        return {
            success: false,
            error: error.message,
            timestamp: new Date().toISOString()
        };
    },

    /**
     * Handle decryption process errors
     * @param {Error} error - The error object
     * @param {string} messageId - ID of the message that failed to decrypt
     */
    handleDecryptionError: (error, messageId) => {
        // Log warning but don't spam toasts for every message in a list
        debugLogger.warn(`Decryption failed for message ${messageId}:`, error);
        monitoringService.logError(error, { type: 'decryption_error', messageId });

        // Return a safe fallback object or error indicator
        return {
            decryptionFailed: true,
            content: '🔒 Message could not be decrypted',
            error: true
        };
    },

    /**
     * Handle key exchange/generation errors
     * @param {Error} error 
     */
    handleKeyError: (error) => {
        debugLogger.critical('Key management error:', error);
        monitoringService.logError(error, { type: 'key_error' });

        toast.error('Security key issue detected. You may need to reconnect.', {
            duration: 5000,
            icon: '🔐'
        });
    }
};

export default encryptionErrorHandler;
